import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import apiClient from '../../api/apiClient';
import { ArrowLeft, Save, Edit, FileText, Users, Upload } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { createPageUrl } from '../../utils';

import PunishmentInfoTab from '../../components/hris/punishment/form/PunishmentInfoTab';
import PunishmentWitnessTab from '../../components/hris/punishment/form/PunishmentWitnessTab';
import PunishmentDocumentTab from '../../components/hris/punishment/form/PunishmentDocumentTab';

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const INITIAL_FORM_DATA = {
  employee_id: '',
  punishment_type: 'verbal_warning',
  violation_category: 'attendance',
  description: '',
  incident_date: getTodayDate(), // Auto-fill with today's date
  effective_date: '',
  end_date: '',
  issued_by: '',
  witness: '',
  validator: '',
  sp_document_url: '',
  bap_document_url: '',
  evidence_url: '',
  notes: '',
};

const TABS = [
  { id: 'info', label: 'Data Hukuman', icon: FileText },
  { id: 'witness', label: 'Saksi & Validator', icon: Users },
  { id: 'document', label: 'Dokumen', icon: Upload },
];

export default function PunishmentForm() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const mode = searchParams.get('mode');
  const isViewMode = mode === 'view';
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [punishmentId, setPunishmentId] = useState<string | null>(id || null);

  const formatDateForInput = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    try {
      // Handle ISO date string (e.g., "2025-12-21T17:00:00.000Z")
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        // If it's not a valid date, try parsing as YYYY-MM-DD
        const parts = dateStr.split('T')[0].split('-');
        if (parts.length === 3) {
          return dateStr.split('T')[0];
        }
        return '';
      }
      // Convert to YYYY-MM-DD format for input type="date"
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      // If it's already in YYYY-MM-DD format, return as is
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateStr;
      }
      return '';
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const empData = await apiClient.get('/hris/employees');
      setEmployees(empData.employees || empData || []);

      if (isEdit) {
        const item = await apiClient.get(`/hris/punishments/${id}`);
        if (item) {
          setFormData({
            ...INITIAL_FORM_DATA,
            ...item,
            incident_date: formatDateForInput(item.incident_date),
            effective_date: formatDateForInput(item.effective_date),
            end_date: formatDateForInput(item.end_date),
          });
        }
      }
    } catch (error) {
      // Error loading data
    } finally {
      setLoading(false);
    }
  }, [isEdit, id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Validate required fields
    if (
      !formData.employee_id ||
      formData.employee_id === '' ||
      formData.employee_id === null ||
      formData.employee_id === undefined
    ) {
      showError('Pegawai harus dipilih');
      setActiveTab('info'); // Switch to info tab to show the error
      return;
    }
    if (!formData.incident_date || formData.incident_date.trim() === '') {
      showError('Tanggal kejadian harus diisi');
      setActiveTab('info'); // Switch to info tab to show the error
      return;
    }

    setSaving(true);
    try {
      let currentId = punishmentId || id;

      if (!currentId) {
        // Create new punishment first
        const response = await apiClient.post('/hris/punishments', formData);
        currentId = response.id || response.punishment?.id || response.data?.id;
        if (currentId) {
          setPunishmentId(currentId);
          // Update URL with new ID
          window.history.replaceState({}, '', `?id=${currentId}`);
        }
      }

      if (currentId) {
        // Update existing punishment
        await apiClient.put(`/hris/punishments/${currentId}`, formData);
        showSuccess('Data hukuman berhasil disimpan');
        setTimeout(() => {
          navigate('/punishmentlist');
        }, 1000);
      } else {
        showError('Gagal menyimpan data');
      }
    } catch (error: any) {
      showError(error?.message || 'Gagal menyimpan data');
    } finally {
      setSaving(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'info':
        return (
          <PunishmentInfoTab
            formData={formData}
            handleChange={handleChange}
            employees={employees}
            readonly={isViewMode}
          />
        );
      case 'witness':
        return (
          <PunishmentWitnessTab
            formData={formData}
            handleChange={handleChange}
            employees={employees}
            readonly={isViewMode}
          />
        );
      case 'document':
        return (
          <PunishmentDocumentTab
            formData={formData}
            handleChange={handleChange}
            readonly={isViewMode}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-300"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isViewMode ? 'Lihat' : isEdit ? 'Edit' : 'Tambah'} Punishment
          </h1>
          <p className="text-gray-500 mt-1">Data hukuman disiplin pegawai</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Tabs */}
        <div className="bg-gray-100 p-1 rounded-lg flex flex-wrap gap-1 mb-6">
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-red-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-sm border p-6">{renderTabContent()}</div>

        {/* Action Buttons - Outside Card */}
        {!isViewMode && (
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-300"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 active:scale-95 flex items-center gap-2 disabled:opacity-50 transition-all duration-300"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        )}
        {isViewMode && (
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-300"
            >
              Kembali
            </button>
            <Link
              to={createPageUrl(`PunishmentForm?id=${id}`)}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 active:scale-95 flex items-center gap-2 transition-all duration-300"
            >
              <Edit className="w-4 h-4" />
              Edit
            </Link>
          </div>
        )}
      </form>
    </div>
  );
}
